import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert,
  Image
} from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { partnersAPI } from '../services/api';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Icon from '../components/Icon';

// Import icons as components for proper TypeScript support
import * as Icons from 'react-icons/fa';

interface PartnerFormValues {
  name: string;
  nameAr: string;
  website: string;
  description: string;
}

// Validation schema
const PartnerSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  website: Yup.string().url('Invalid URL format'),
  description: Yup.string(),
});

const PartnerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [existingLogo, setExistingLogo] = useState<string>('');
  
  // Convert initialValues to a state variable
  const [initialValues, setInitialValues] = useState<PartnerFormValues>({
    name: '',
    nameAr: '',
    website: '',
    description: ''
  });

  // Fetch partner data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchPartner = async () => {
        try {
          const response = await partnersAPI.getPartnerById(id);
          const partner = response.data;
          
          // Update initialValues using setState
          setInitialValues({
            name: partner.name || '',
            nameAr: partner.nameAr || '',
            website: partner.website || '',
            description: partner.description || '',
          });
          
          // Set existing logo
          if (partner.logo) {
            setExistingLogo(`${partner.logo.secure_url}`);
          }
          
          setLoading(false);
        } catch (err: any) {
          setError('Failed to fetch partner details');
          console.error(err);
          setLoading(false);
        }
      };
      
      fetchPartner();
    } else {
      setLoading(false);
    }
  }, [id, isEditMode]);

  // Handle form submission
  const handleSubmit = async (values: PartnerFormValues, { setSubmitting }: any) => {
    setError('');
    setSuccess('');
    setSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('nameAr', values.nameAr);
      formData.append('website', values.website);
      formData.append('description', values.description);
      
      // Append logo if selected
      if (logo) {
        formData.append('images', logo);
      }
      
      let response;
      if (isEditMode && id) {
        response = await partnersAPI.updatePartner(id, formData);
        setSuccess('Partner updated successfully');
      } else {
        response = await partnersAPI.createPartner(formData);
        setSuccess('Partner created successfully');
      }
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/partners');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save partner');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle logo file selection
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogo(file);
      
      // Create preview for the logo
      const reader = new FileReader();
      reader.onload = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove selected logo
  const removeSelectedLogo = () => {
    setLogo(null);
    setLogoPreview('');
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading partner data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="mb-0">{isEditMode ? 'Edit Partner' : 'Add New Partner'}</h1>
        </Col>
        <Col xs="auto">
          <Link to="/partners">
            <Button variant="outline-secondary">
              <Icon icon={Icons.FaArrowLeft} className="me-2" /> Back to Partners
            </Button>
          </Link>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={PartnerSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col lg={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Partner Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.name && !!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Arabic Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="nameAr"
                        value={values.nameAr}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.nameAr && !!errors.nameAr}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.nameAr}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Website URL</Form.Label>
                      <Form.Control
                        type="url"
                        name="website"
                        placeholder="https://example.com"
                        value={values.website}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.website && !!errors.website}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.website}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Optional: Enter the partner's website URL
                      </Form.Text>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.description && !!errors.description}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.description}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  
                  <Col lg={4}>
                    <Card className="bg-light">
                      <Card.Body>
                        <h5 className="mb-3">Partner Logo</h5>
                        
                        {/* Existing Logo */}
                        {existingLogo && !logoPreview && (
                          <div className="mb-3 text-center">
                            <p className="text-muted mb-2">Current Logo</p>
                            <Image 
                              src={existingLogo} 
                              alt="Partner Logo" 
                              className="img-thumbnail mb-2" 
                              style={{ maxHeight: '150px' }}
                            />
                          </div>
                        )}
                        
                        {/* Logo Upload */}
                        <div className="mb-3">
                          <Form.Label>Upload Logo</Form.Label>
                          <Form.Control 
                            type="file" 
                            onChange={handleLogoChange} 
                            accept="image/*"
                          />
                          <Form.Text className="text-muted">
                            Recommended size: 200x200 pixels. Maximum file size: 2MB.
                          </Form.Text>
                        </div>
                        
                        {/* Logo Preview */}
                        {logoPreview && (
                          <div className="mb-3 text-center">
                            <p className="text-muted mb-2">Logo Preview</p>
                            <div className="position-relative d-inline-block">
                              <Image 
                                src={logoPreview} 
                                alt="Logo Preview" 
                                className="img-thumbnail mb-2" 
                                style={{ maxHeight: '150px' }}
                              />
                              <Button 
                                variant="danger" 
                                size="sm" 
                                className="position-absolute top-0 end-0 m-1"
                                onClick={removeSelectedLogo}
                                title="Remove logo"
                              >
                                <Icon icon={Icons.FaTrash} />
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* No Logo Info */}
                        {!existingLogo && !logoPreview && (
                          <div className="text-center p-4 bg-white rounded mb-3">
                            <Icon icon={Icons.FaImage} className="display-4 text-muted mb-3" />
                            <p>No logo selected</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                
                <div className="d-flex justify-content-end mt-4">
                  <Button 
                    variant="secondary" 
                    as={Link as any} 
                    to="/partners" 
                    className="me-2"
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    disabled={isSubmitting}
                  >
                    <Icon icon={Icons.FaSave} className="me-2" />
                    {isSubmitting ? 'Saving...' : 'Save Partner'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PartnerForm; 